/**
 * VideoPlayer Component - 視聴者用ビデオプレイヤー
 * - 音声ミュート/アンミュート機能
 * - 全画面表示機能
 */

import { useState, useRef, useEffect, RefObject, useImperativeHandle, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';

interface VideoPlayerProps {
    videoRef: RefObject<HTMLVideoElement | null>;
    showPointsExhausted?: boolean;
    isBroadcastPaused?: boolean;
    isBroadcastSuspended?: boolean;
    suspendedCountdown?: number | null;
    /** Hide internal controls (when using external controls) */
    hideControls?: boolean;
    /** Callback when mute state changes */
    onMuteChange?: (isMuted: boolean) => void;
    /** Callback when fullscreen state changes */
    onFullscreenChange?: (isFullscreen: boolean) => void;
}

/** Ref handle for external control */
export interface VideoPlayerHandle {
    toggleMute: () => void;
    toggleFullscreen: () => Promise<void>;
    isMuted: boolean;
    isFullscreen: boolean;
}

const VideoPlayer = forwardRef<VideoPlayerHandle, VideoPlayerProps>(function VideoPlayer({
    videoRef,
    showPointsExhausted = false,
    isBroadcastPaused = false,
    isBroadcastSuspended = false,
    hideControls = false,
    onMuteChange,
    onFullscreenChange,
}, ref) {
    const { t } = useTranslation('watch');
    // Start muted to comply with browser autoplay policy
    const [isMuted, setIsMuted] = useState(true);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [isBuffering, setIsBuffering] = useState(true); // 初期状態はバッファリング中
    const containerRef = useRef<HTMLDivElement>(null);

    // バッファリング状態の検出
    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        const handleWaiting = () => {
            console.log('[VideoPlayer] Buffering started');
            setIsBuffering(true);
        };

        const handlePlaying = () => {
            console.log('[VideoPlayer] Playback started');
            setIsBuffering(false);
        };

        const handleCanPlay = () => {
            console.log('[VideoPlayer] Can play');
            // canplayだけでは不十分な場合があるので、playingイベントも併用
        };

        const handleStalled = () => {
            console.log('[VideoPlayer] Playback stalled');
            setIsBuffering(true);
        };

        video.addEventListener('waiting', handleWaiting);
        video.addEventListener('playing', handlePlaying);
        video.addEventListener('canplay', handleCanPlay);
        video.addEventListener('stalled', handleStalled);

        // 初期状態チェック: すでに再生中ならバッファリング解除
        if (video.readyState >= 3 && !video.paused) {
            setIsBuffering(false);
        }

        return () => {
            video.removeEventListener('waiting', handleWaiting);
            video.removeEventListener('playing', handlePlaying);
            video.removeEventListener('canplay', handleCanPlay);
            video.removeEventListener('stalled', handleStalled);
        };
    }, [videoRef]);

    // Sync muted state with video element (also mute when broadcast is paused or suspended)
    useEffect(() => {
        if (videoRef.current) {
            const shouldMute = isMuted || isBroadcastPaused || isBroadcastSuspended;
            videoRef.current.muted = shouldMute;
            console.log('[VideoPlayer] Mute state:', { isMuted, shouldMute, actualMuted: videoRef.current.muted });
        }
    }, [isMuted, isBroadcastPaused, isBroadcastSuspended, videoRef]);

    // Monitor actual video element muted state and sync with our state
    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        const syncMutedState = () => {
            const actualMuted = video.muted;
            // Only sync if browser changed the muted state unexpectedly
            if (actualMuted !== isMuted && !isBroadcastPaused && !isBroadcastSuspended) {
                console.log('[VideoPlayer] Browser changed mute state:', { actualMuted, isMuted });
                setIsMuted(actualMuted);
                onMuteChange?.(actualMuted);
            }
        };

        // Check on volume change events (browser may auto-mute)
        video.addEventListener('volumechange', syncMutedState);

        return () => {
            video.removeEventListener('volumechange', syncMutedState);
        };
    }, [videoRef, isMuted, isBroadcastPaused, isBroadcastSuspended, onMuteChange]);

    // Listen for fullscreen changes (including ESC key exit)
    useEffect(() => {
        const handleFullscreenChange = () => {
            const newIsFullscreen = !!document.fullscreenElement;
            setIsFullscreen(newIsFullscreen);
            onFullscreenChange?.(newIsFullscreen);
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
        };
    }, [onFullscreenChange]);

    const toggleMute = () => {
        const newIsMuted = !isMuted;
        setIsMuted(newIsMuted);
        onMuteChange?.(newIsMuted);
    };

    const toggleFullscreen = async () => {
        if (!containerRef.current) return;

        try {
            if (!document.fullscreenElement) {
                await containerRef.current.requestFullscreen();
            } else {
                await document.exitFullscreen();
            }
        } catch (error) {
            console.error('Fullscreen error:', error);
        }
    };

    // Expose methods and state to parent via ref
    useImperativeHandle(ref, () => ({
        toggleMute,
        toggleFullscreen,
        isMuted,
        isFullscreen,
    }), [isMuted, isFullscreen]);

    return (
        <div ref={containerRef} className="aspect-video bg-black relative group">
            <video
                ref={videoRef}
                playsInline
                autoPlay
                muted={isMuted || isBroadcastPaused || isBroadcastSuspended}
                className={`w-full h-full bg-black ${(showPointsExhausted || isBroadcastPaused || isBroadcastSuspended) ? 'hidden' : ''}`}
            />

            {/* Points Exhausted Overlay */}
            {showPointsExhausted && (
                <div className="absolute inset-0 bg-gradient-to-b from-gray-800 to-gray-900 flex flex-col items-center justify-center text-white">
                    <div className="text-6xl mb-4">💰</div>
                    <div className="text-xl font-bold mb-2">{t('videoPlayer.pointsInsufficient')}</div>
                    <div className="text-gray-400 text-sm">
                        {t('videoPlayer.purchasePrompt')}
                    </div>
                </div>
            )}

            {/* Buffering Overlay - 配信データ読み込み中 */}
            {isBuffering && !showPointsExhausted && !isBroadcastPaused && !isBroadcastSuspended && (
                <div className="absolute inset-0 bg-black flex flex-col items-center justify-center text-white">
                    <div className="relative">
                        <div className="w-16 h-16 border-4 border-gray-600 border-t-pink-500 rounded-full animate-spin"></div>
                    </div>
                    <div className="mt-4 text-lg font-medium">{t('videoPlayer.loading', '読み込み中...')}</div>
                    <div className="text-gray-400 text-sm mt-1">{t('videoPlayer.loadingMessage', 'しばらくお待ちください')}</div>
                </div>
            )}

            {/* Video Controls - visible on hover or fullscreen (can be hidden) */}
            {!hideControls && (
                <div className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 flex items-center justify-end gap-3 transition-opacity ${isFullscreen ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
                    {/* Mute/Unmute Button */}
                    <button
                        onClick={toggleMute}
                        className="bg-white/20 hover:bg-white/30 text-white w-10 h-10 flex items-center justify-center transition-colors"
                        title={isMuted ? t('videoPlayer.muteOn') : t('videoPlayer.muteOff')}
                    >
                        {isMuted ? (
                            // Muted icon
                            <svg width="20" height="20" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217zM12.293 7.293a1 1 0 011.414 0L15 8.586l1.293-1.293a1 1 0 111.414 1.414L16.414 10l1.293 1.293a1 1 0 01-1.414 1.414L15 11.414l-1.293 1.293a1 1 0 01-1.414-1.414L13.586 10l-1.293-1.293a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                        ) : (
                            // Unmuted icon
                            <svg width="20" height="20" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217zM14.657 2.929a1 1 0 011.414 0A9.972 9.972 0 0119 10a9.972 9.972 0 01-2.929 7.071 1 1 0 01-1.414-1.414A7.971 7.971 0 0017 10c0-2.21-.894-4.208-2.343-5.657a1 1 0 010-1.414zm-2.829 2.828a1 1 0 011.415 0A5.983 5.983 0 0115 10a5.984 5.984 0 01-1.757 4.243 1 1 0 01-1.415-1.415A3.984 3.984 0 0013 10a3.983 3.983 0 00-1.172-2.828 1 1 0 010-1.415z" clipRule="evenodd" />
                            </svg>
                        )}
                    </button>

                    {/* Fullscreen Button */}
                    <button
                        onClick={toggleFullscreen}
                        className="bg-white/20 hover:bg-white/30 text-white w-10 h-10 flex items-center justify-center transition-colors"
                        title={isFullscreen ? t('videoPlayer.exitFullscreen') : t('videoPlayer.enterFullscreen')}
                    >
                        {isFullscreen ? (
                            // Exit fullscreen icon
                            <svg width="20" height="20" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M3.28 2.22a.75.75 0 00-1.06 1.06L5.44 6.5H3.75a.75.75 0 000 1.5h4.5a.75.75 0 00.75-.75v-4.5a.75.75 0 00-1.5 0v1.69L4.22 1.16a.75.75 0 00-1.06 0l.12.06zM12.75 6.5a.75.75 0 000 1.5h4.5a.75.75 0 00.75-.75v-4.5a.75.75 0 00-1.5 0v1.69l-3.22-3.22a.75.75 0 10-1.06 1.06l3.22 3.22h-1.69zM3.75 12a.75.75 0 00-.75.75v4.5c0 .414.336.75.75.75h4.5a.75.75 0 000-1.5H6.56l3.22-3.22a.75.75 0 10-1.06-1.06L5.5 15.44v-1.69a.75.75 0 00-.75-.75zm9-.75a.75.75 0 011.06 0l3.22 3.22v-1.69a.75.75 0 011.5 0v4.5a.75.75 0 01-.75.75h-4.5a.75.75 0 010-1.5h1.69l-3.22-3.22a.75.75 0 010-1.06z" />
                            </svg>
                        ) : (
                            // Enter fullscreen icon
                            <svg width="20" height="20" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M3 4a1 1 0 011-1h4a1 1 0 010 2H6.414l2.293 2.293a1 1 0 01-1.414 1.414L5 6.414V8a1 1 0 01-2 0V4zm9 1a1 1 0 010-2h4a1 1 0 011 1v4a1 1 0 01-2 0V6.414l-2.293 2.293a1 1 0 11-1.414-1.414L13.586 5H12zm-9 7a1 1 0 012 0v1.586l2.293-2.293a1 1 0 011.414 1.414L6.414 15H8a1 1 0 010 2H4a1 1 0 01-1-1v-4zm13-1a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 010-2h1.586l-2.293-2.293a1 1 0 011.414-1.414L15 13.586V12a1 1 0 011-1z" />
                            </svg>
                        )}
                    </button>
                </div>
            )}
        </div>
    );
});

export default VideoPlayer;
